import React, { useCallback, useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import VerticalSpacing from "@cayuse/shared-react.components.vertical-spacing";
import { isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";
import PropTypes from "prop-types";

import FormResponse from "components/FormResponse";
import Modifications from "containers/Modifications";
import {
	API_AWARD,
	AWARD_REVIEW_STATUS,
	COLUMN_HEADER_KEYS,
	MODIFICATION_STATUS,
	ROLE_SP_AWARD_ADMINISTRATOR,
	ROLE_SP_SYSTEM_ADMINISTRATOR,
	DEFAULT_AWARD_COLUMN_HEADERS,
	ROLE_SP_AWARD_EDITOR,
	AWARDS,
} from "globals/constants";
import {
	useDictionary,
	useFormAccesses,
	useHasRoles,
	useColumnHeaders,
	useRecordRevisionAccess,
} from "hooks";
import {
	getAward,
	getAwardValues as getValues,
	updateAwardOrProperty,
} from "store/actions/award";
import { getFormModifications, refreshFormModifications } from "store/actions/modifications";
import { awardSelectors, modificationListSelectors } from "store/selectors";
import { getCaseInstanceId } from "utils";

const AWARDS_LIST_DEFAULT_COLUMNS = [
	DEFAULT_AWARD_COLUMN_HEADERS.AWARD_NUMBER,
	DEFAULT_AWARD_COLUMN_HEADERS.PI,
	DEFAULT_AWARD_COLUMN_HEADERS.PROJECT_TITLE,
	DEFAULT_AWARD_COLUMN_HEADERS.SPONSOR,
	DEFAULT_AWARD_COLUMN_HEADERS.SPONSOR_AWARD_NUMBER,
	DEFAULT_AWARD_COLUMN_HEADERS.PRIME_SPONSOR,
	DEFAULT_AWARD_COLUMN_HEADERS.INSTRUMENT_TYPE,
	DEFAULT_AWARD_COLUMN_HEADERS.AWARD_START_DATE,
	DEFAULT_AWARD_COLUMN_HEADERS.AWARD_END_DATE,
	DEFAULT_AWARD_COLUMN_HEADERS.ADMIN_UNIT,
	DEFAULT_AWARD_COLUMN_HEADERS.AWARD_AMOUNT,
];

const AwardFormView = props => {
	const { latestModificationNumber = 0 } = useSelector(
		modificationListSelectors.content
	);
	const award = useSelector(awardSelectors.award);

	const { columnHeaders: availableAwardColumns } = useColumnHeaders(
		COLUMN_HEADER_KEYS.AWARD,
		{
			defaultColumnHeaders: AWARDS_LIST_DEFAULT_COLUMNS,
		}
	);

	const [ currentModification, setCurrentModification ] = useState({});

	const isLatestModification = parseInt(currentModification.modificationNumber) === parseInt(latestModificationNumber);

	//const hasDraft = useMemo(() => modificationList.some(item => item.modificationStatus === MODIFICATION_STATUS.draft), [ modificationList ]);

	// const disableEditButttons = useMemo(() => {
	//     return (hasDraft ?
	//         parseInt(currentModification.modificationNumber) !==
	//         parseInt(latestModificationNumber) - 1 :
	//         parseInt(currentModification.modificationNumber) !==
	//         parseInt(latestModificationNumber)
	//     );
	// }, [currentModification.modificationNumber, latestModificationNumber]);

	const [ isModificationChange, setIsModificationChange ] = useState(false);
	const [ shouldUpdateForm, setShouldUpdateForm ] = useState(false);
	const {
		[ROLE_SP_AWARD_ADMINISTRATOR]: isAwardAdmin,
		[ROLE_SP_SYSTEM_ADMINISTRATOR]: isSystemAdmin,
		[ROLE_SP_AWARD_EDITOR]: isAwardEditor,
	} = useHasRoles([
		ROLE_SP_AWARD_ADMINISTRATOR,
		ROLE_SP_SYSTEM_ADMINISTRATOR,
		ROLE_SP_AWARD_EDITOR,
	]);

	const dictionary = useDictionary("common.form", {
		sectionTitle: "Award Sections",
	});

	const { awardId: loadedId } = useParams();

	const formAccesses = useFormAccesses("award", award.awardId);

	const disableEditButtons = useMemo(() => {
		return !formAccesses.systemAdmin && award.statusName === AWARD_REVIEW_STATUS.closeout;
	}, [ formAccesses, award ]);

	const hasRecordRevisionEditAccess = useRecordRevisionAccess(
		COLUMN_HEADER_KEYS.AWARD,
		award.statusName,
		formAccesses,
		isLatestModification
	) && (currentModification.modificationStatus !== MODIFICATION_STATUS.draft);

	const canModifyAward =
		(award.statusName === AWARD_REVIEW_STATUS.active ||
			award.statusName === AWARD_REVIEW_STATUS.closeout) &&
		currentModification.modificationStatus === MODIFICATION_STATUS.draft &&
		(isAwardAdmin || isAwardEditor || isSystemAdmin);

	const statusIsEditable =
		(award.statusName === AWARD_REVIEW_STATUS.setup ||
			award.statusName === AWARD_REVIEW_STATUS.inReview) &&
		(isLatestModification || award.modificationNumber === null);
	const isEditableStatus = statusIsEditable || canModifyAward;

	const canEdit = isEditableStatus && formAccesses.updateForm;

	const onFormSave = useCallback(() => {
		getValues({
			awardId: award.awardId,
			fieldKeys: [], // todo: align with whatever Summary needs to fetch
		});
		refreshFormModifications();
	}, [ award.awardId ]);

	const reloadAward = useCallback(() => {
		getAward(award.awardId, { isRefresh: true });
	}, [ award.awardId ]);

	useEffect(() => {
		const fetchData = async () => {
			const formInfo = {
				formId: loadedId,
				formType: AWARDS,
			};
			const response = await getFormModifications({ formInfo });
			const caseInstanceId = getCaseInstanceId(response.content);
			await getAward(loadedId, { caseInstanceId, isRefresh: false });
		};

		fetchData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ loadedId ]);

	useEffect(() => {
		if (
			isNonEmptyString(currentModification.caseInstanceId) &&
			currentModification.caseInstanceId !== award.caseInstanceId &&
			award.formElements
		) {
			updateAwardOrProperty({
				...award,
				...currentModification,
				adminTeamMembers: award.adminTeamMembers,
			});

			setShouldUpdateForm(true);
		}
	}, [ award, currentModification ]);

	return (
		<>
			{award.modificationNumber !== null && (
				<VerticalSpacing>
					<Modifications
						key={loadedId}
						currentModification={currentModification}
						caseInstanceId={
							isNonEmptyString(currentModification.caseInstanceId)
								? currentModification.caseInstanceId
								: award.caseInstanceId
						}
						formType={COLUMN_HEADER_KEYS.AWARD}
						formId={award.awardId}
						formApi={`${API_AWARD}/${award.awardId}`}
						formStatus={award.statusName}
						reviewStatus={award.reviewStatus}
						isFormAdmin={formAccesses.manage || formAccesses.editor}
						canReroute={formAccesses.changeStatus}
						setCurrentModification={setCurrentModification}
						setIsModificationChange={setIsModificationChange}
						availabletModificationsColumns={availableAwardColumns}
						setShouldUpdateForm={setShouldUpdateForm}
					/>
				</VerticalSpacing>
			)}
			<FormResponse
				key={loadedId}
				canEdit={canEdit}
				dictionary={dictionary}
				form={award}
				formIdKey="awardId"
				formType={COLUMN_HEADER_KEYS.AWARD}
				isFormAdmin={isAwardAdmin || isSystemAdmin || false}
				loadedFormId={loadedId}
				onFlowableFormSave={onFormSave}
				payloadRootContentKey={"awardContent"}
				saveOnChange={true}
				saveValuesApiUrl={`/sp/api/awards/${award.awardId}`}
				setIsModificationChange={setIsModificationChange}
				isModificationChange={isModificationChange}
				setShouldUpdateForm={setShouldUpdateForm}
				shouldUpdateForm={shouldUpdateForm}
				statusId={award.statusKey}
				recordRevisionEditMode={hasRecordRevisionEditAccess}
				onReload={reloadAward}
				disableEditButtons={disableEditButtons}
				{...props}
			/>
		</>
	);
};

AwardFormView.propTypes = {
	handleFormLoaded: PropTypes.func,
	setFormVariables: PropTypes.func,
	formVariables: PropTypes.object,
};

export default AwardFormView;
